import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentUser } from '../../../features/auth';
import { AVAILABLE_LANGUAGES } from '../../routing/navigationConfig';
import { generateAvatarUrl } from './utils/avatar';

/**
 * User dropdown menu in the sidebar footer
 * Includes user info, settings, language selector, and logout
 * @param {Object} props
 * @param {Function} props.onLogout - Logout handler function
 */
export function SidebarUserDropdown({ onLogout }) {
  const { t, i18n } = useTranslation();
  const userData = useAppSelector(selectCurrentUser);

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
  };

  const avatarUrl = generateAvatarUrl(userData?.first_name, userData?.last_name);
  const userName = `${userData?.first_name || 'Usuario'} ${userData?.last_name || ''}`;
  const userEmail = userData?.email || 'user@example.com';

  return (
    <div className="hs-dropdown [--strategy:absolute] [--placement:top-start] [--auto-close:inside] relative inline-flex w-full hs-overlay-minified:w-auto">
      {/* Dropdown Trigger */}
      <button
        id="hs-sidebar-dropdown"
        type="button"
        className="cursor-pointer hs-dropdown-toggle py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg text-muted hover:bg-brand/5 focus:outline-none focus:bg-brand/5 disabled:opacity-50 disabled:pointer-events-none w-full hs-overlay-minified:justify-center hs-overlay-minified:p-0 hs-overlay-minified:w-auto hs-overlay-minified:h-auto hs-overlay-minified:hover:bg-transparent"
        aria-haspopup="menu"
        aria-expanded="false"
        aria-label="Dropdown"
      >
        <img
          className="shrink-0 size-8 rounded-full object-cover min-w-8 min-h-8"
          src={avatarUrl}
          alt="Avatar"
        />
        <span className="hs-overlay-minified:hidden text-muted font-medium truncate max-w-[7.5rem]">
          <div>{userName}</div>
        </span>
        <Icon
          icon="lucide:chevron-down"
          className="hs-overlay-minified:hidden hs-dropdown-open:rotate-180 ms-auto shrink-0 size-4 text-muted"
        />
      </button>

      {/* Dropdown Menu */}
      <div
        className="hs-dropdown-menu transition-[opacity,margin] duration hs-dropdown-open:opacity-100 opacity-0 hidden min-w-72 bg-white shadow-xl rounded-2xl mb-2 p-4 border border-zinc-200 z-[70]"
        role="menu"
        aria-orientation="vertical"
        aria-labelledby="hs-sidebar-dropdown"
      >
        {/* User Header */}
        <div className="flex items-center gap-x-3 pb-4 border-b border-zinc-200">
          <img
            className="shrink-0 size-10 rounded-full object-cover"
            src={avatarUrl}
            alt="Avatar"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-brand truncate">
              {userName}
            </p>
            <p className="text-xs text-muted truncate">
              {userEmail}
            </p>
          </div>
        </div>

        {/* Language Selector */}
        <div className="py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted">{t('userMenu.language')}</span>
          </div>
          <div className="space-y-1">
            {AVAILABLE_LANGUAGES.map((lang) => (
              <button
                key={lang}
                onClick={() => handleLanguageChange(lang)}
                className={`cursor-pointer w-full flex items-center justify-between py-2 px-3 rounded-lg text-sm transition-colors ${
                  i18n.language === lang
                    ? 'text-white'
                    : 'text-muted hover:bg-brand/5'
                }`}
                style={i18n.language === lang ? { backgroundColor: 'var(--color-primary)' } : {}}
              >
                <span>{t(`language.${lang}`)}</span>
                {i18n.language === lang && (
                  <Icon icon="lucide:check" className="size-4" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Logout Button */}
        <div className="pt-3 border-t border-zinc-200">
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-x-3 py-2 px-3 rounded-lg text-sm text-red-600 hover:bg-red-50 focus:outline-none focus:bg-red-50"
          >
            <Icon icon="lucide:log-out" className="shrink-0 size-5" />
            <span className="font-medium">{t('userMenu.logout')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
